import React, { useEffect, useState } from 'react';
import { useNavigate } from 'react-router-dom';
import { Plus, Pencil, Trash2, Power, PowerOff } from 'lucide-react';
import api from '../services/api';
import { useAuth } from '../context/AuthContext';
import Modal from '../components/Modal';
import BotonAyuda from '../components/BotonAyuda';

// Vendedores (#153): entidad propia sin login, con una matriz de comisión
// por producto × tipo_precio (detal/mayor/fábrica) y liquidaciones a pedido.
// Cada cliente queda asignado a un vendedor de forma fija (ver VendedorDetalle).
const FORM_VACIO = { nombre: '', activo: true };

export default function Vendedores() {
  const navigate = useNavigate();
  const { empresaActiva, empresas } = useAuth();
  const empresaNombre = empresas.find((e) => String(e.empresa_id) === String(empresaActiva))?.empresa_nombre;

  const [vendedores, setVendedores] = useState([]);
  const [mostrarForm, setMostrarForm] = useState(false);
  const [editandoId, setEditandoId] = useState(null);
  const [form, setForm] = useState(FORM_VACIO);
  const [error, setError] = useState('');

  async function cargar() {
    const { data } = await api.get('/vendedores');
    setVendedores(data);
  }

  useEffect(() => {
    if (empresaActiva) cargar();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [empresaActiva]);

  function abrirNuevo() {
    setEditandoId(null);
    setForm(FORM_VACIO);
    setError('');
    setMostrarForm(true);
  }

  function abrirEdicion(v, e) {
    e.stopPropagation();
    setEditandoId(v.id);
    setForm({ nombre: v.nombre, activo: !!v.activo });
    setError('');
    setMostrarForm(true);
  }

  function cancelar() {
    setMostrarForm(false);
    setEditandoId(null);
    setForm(FORM_VACIO);
    setError('');
  }

  async function guardar(e) {
    e.preventDefault();
    setError('');
    if (!form.nombre.trim()) return;
    try {
      if (editandoId) {
        await api.put(`/vendedores/${editandoId}`, form);
      } else {
        await api.post('/vendedores', { nombre: form.nombre });
      }
      cancelar();
      cargar();
    } catch (err) {
      setError(err.response?.data?.error || 'No se pudo guardar el vendedor');
    }
  }

  async function alternarActivo(v, e) {
    e.stopPropagation();
    try {
      await api.put(`/vendedores/${v.id}`, { activo: v.activo ? 0 : 1 });
      cargar();
    } catch (err) {
      alert(err.response?.data?.error || 'No se pudo cambiar el estado');
    }
  }

  async function eliminar(v, e) {
    e.stopPropagation();
    if (!window.confirm(`¿Eliminar al vendedor "${v.nombre}"? Solo se puede si no tiene clientes asignados.`)) return;
    try {
      await api.delete(`/vendedores/${v.id}`);
      cargar();
    } catch (err) {
      alert(err.response?.data?.error || 'No se pudo eliminar el vendedor');
    }
  }

  return (
    <div className="space-y-6">
      <div className="flex flex-col gap-3 sm:flex-row sm:items-center sm:justify-between">
        <h1 className="text-2xl font-bold text-slate-800">
          Vendedores
          {empresaNombre && <span className="text-slate-400 font-normal"> · {empresaNombre}</span>}
          <BotonAyuda />
        </h1>
        <div className="flex items-center gap-2 flex-wrap">
          <button
            onClick={abrirNuevo}
            title="Nuevo vendedor"
            className="p-2 rounded-md text-white bg-primary-600 hover:bg-primary-700 transition-colors"
          >
            <Plus size={18} />
          </button>
        </div>
      </div>

      {mostrarForm && (
        <Modal title={editandoId ? 'Editando vendedor' : 'Nuevo vendedor'} onClose={cancelar}>
          <form onSubmit={guardar} className="grid grid-cols-1 gap-4">
            <div>
              <label className="block text-xs text-slate-500 mb-1">Nombre</label>
              <input
                autoFocus
                required
                value={form.nombre}
                onChange={(e) => setForm({ ...form, nombre: e.target.value })}
                className="border rounded-md px-3 py-2 w-full"
              />
            </div>
            {editandoId && (
              <label className="flex items-center gap-2 text-sm text-slate-600">
                <input
                  type="checkbox"
                  checked={form.activo}
                  onChange={(e) => setForm({ ...form, activo: e.target.checked })}
                  className="rounded border-slate-300"
                />
                Activo
              </label>
            )}
            {error && <p className="text-red-600 text-sm">{error}</p>}
            <button type="submit" className="bg-primary-600 text-white rounded-md py-2 hover:bg-primary-700">
              {editandoId ? 'Guardar cambios' : 'Guardar'}
            </button>
          </form>
        </Modal>
      )}

      <div className="bg-white rounded-xl shadow-sm overflow-hidden">
        <div className="overflow-x-auto">
          <table className="w-full text-sm">
            <thead className="bg-slate-50 text-slate-500 text-left">
              <tr>
                <th className="p-3">Nombre</th>
                <th className="p-3 text-right">Clientes</th>
                <th className="p-3">Estado</th>
                <th className="p-3"></th>
              </tr>
            </thead>
            <tbody>
              {vendedores.map((v) => (
                <tr
                  key={v.id}
                  onClick={() => navigate(`/vendedores/${v.id}`)}
                  className="border-t hover:bg-slate-50 transition-colors cursor-pointer"
                >
                  <td className="p-3 font-medium whitespace-nowrap">{v.nombre}</td>
                  <td className="p-3 text-right tabular-nums">{v.clientes_count}</td>
                  <td className="p-3">
                    <span className={`text-xs px-2 py-1 rounded-full whitespace-nowrap ${v.activo ? 'bg-green-50 text-green-700' : 'bg-slate-100 text-slate-500'}`}>
                      {v.activo ? 'Activo' : 'Inhabilitado'}
                    </span>
                  </td>
                  <td className="p-3">
                    <div className="flex items-center gap-3 justify-end" onClick={(e) => e.stopPropagation()}>
                      <button
                        onClick={(e) => alternarActivo(v, e)}
                        title={v.activo ? 'Inhabilitar' : 'Activar'}
                        className={`transition-colors ${v.activo ? 'text-slate-400 hover:text-red-600' : 'text-slate-400 hover:text-green-600'}`}
                      >
                        {v.activo ? <PowerOff size={16} /> : <Power size={16} />}
                      </button>
                      <button onClick={(e) => abrirEdicion(v, e)} title="Editar" className="text-slate-400 hover:text-primary-600 transition-colors">
                        <Pencil size={16} />
                      </button>
                      <button onClick={(e) => eliminar(v, e)} title="Eliminar" className="text-slate-400 hover:text-red-600 transition-colors">
                        <Trash2 size={16} />
                      </button>
                    </div>
                  </td>
                </tr>
              ))}
              {vendedores.length === 0 && (
                <tr>
                  <td colSpan={4} className="p-6 text-center text-slate-400">
                    No hay vendedores creados todavía.
                  </td>
                </tr>
              )}
            </tbody>
          </table>
        </div>
      </div>
    </div>
  );
}
